// @code-guard: skip-file reason="Parser aggregates related promo recommendation parsers; split into per-domain files planned in a follow-up refactor"
import { colorConstants } from 'config/colorConstants';
import { frequencyLabelAltMapping } from 'config/constants';
import dayjs from 'dayjs';
import _ from 'lodash';
import { monthData } from 'utils/Calender';
import { formatNumWithKMBAlt, normalizePromoTypeColorMapping } from 'utils/helperMethods';

// converts the backend metrics list [{ name, value }] into object { totalGsv: value, ... }
export const flattenMetrics = (metrics: any = {}) =>
    Array.isArray(metrics)
        ? metrics.reduce((acc: any, metric: any) => {
              if (metric?.name) acc[_.camelCase(metric.name)] = metric.value;
              return acc;
          }, {})
        : {};

// flattens eventDetailsData.metrics into camelCase fields so the edit/save flow
// (spendTopForm / leverKeys) keeps working.
// returns a clone - never mutate Apollo's (frozen) response objects.
export const flattenEventDetails = (data: any[] = []) =>
    data.map((event: any) => ({
        ...event,
        eventDetailsData: {
            ...event.eventDetailsData,
            ...flattenMetrics(event.eventDetailsData?.metrics),
        },
    }));

export const getEventsMetricMeta = (eventsData: any[] = [], config: any) => {
    const { promoRecommendation } = config;
    const metrics = eventsData?.[0]?.visibleEventData?.eventHoverData?.metrics || [];
    const leadingColumns = (promoRecommendation?.overlayKeys || []).slice(0, 4);

    const metricMeta: any = {};
    const metricLabels: string[] = [];
    metrics.forEach((metric: any) => {
        metricMeta[metric.label] = {
            arrowConfig: metric.arrowConfig,
            pre: metric.pre || '',
            post: metric.post || '',
        };
        metricLabels.push(metric.label);
    });

    //tooltip shows the 4 leading descriptive columns (config) + every metric (backend order)
    const tooltipMapping = [
        ...leadingColumns.map((item: any) => ({ label: item.label, dataKey: item.dataKey })),
        ...metrics.map((metric: any) => ({ label: metric.label, dataKey: metric.name })),
    ];

    return { metricMeta, metricLabels, tooltipMapping };
};

export const eventsListDataParser = (data: any[]) => {
    try {
        const result = data.map((event: any) => {
            const { visibleEventData, eventDetailsData } = event;

            return {
                Account: visibleEventData?.eventBasicFields?.dim2AttributeNames || [],
                'All Event': visibleEventData?.eventBasicFields?.eventName,
                Products: visibleEventData?.eventBasicFields?.dim1AttributeNames || [],
                edit: eventDetailsData,
                key: visibleEventData?.eventBasicFields?.eventId,
                isActive: visibleEventData?.eventBasicFields?.activeStatus,
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
};

//calender parser
export const recommendationCalenderTableParser = (payload: any = {}, config: any, currentFrequency: any) => {
    const { promoRecommendation, commonConfig } = config;
    const promoTypeColorMapping: any = normalizePromoTypeColorMapping(promoRecommendation.promoTypeColorMapping);
    const { data, startWeek = [], endWeek = [] } = payload;
    const result: any = [];
    //console.log(startWeek, endWeek, data, 'logging g');
    const weekPercentage = 25;
    const marginBlock = 10;
    const barHeight = 8;
    const weekDivision = 4;
    try {
        data.forEach((event: any, index: number) => {
            const { visibleEventData } = event;

            const tempObj: any = {
                event: visibleEventData?.eventBasicFields?.eventName,
                // event: `${event.dim1}`,
                key: visibleEventData?.eventBasicFields?.eventId,
                isActive: visibleEventData?.eventBasicFields?.activeStatus,
            };
            for (let i = 0; i < endWeek.length; i += weekDivision) {
                tempObj[dayjs(endWeek[i]).format(commonConfig?.dateFormatAlt)] = [];
            }
            // sorting trends in ascending order of startdate
            // const sortedArray = event.activeTrends.slice().sort(function (a: any, b: any) {
            //     return new Date(a.startTimestamp).getTime() - new Date(b.startTimestamp).getTime();
            // });
            let previousEndIndex = 0;
            let startMonthIndexMapping = 0;
            let updatedMarginTop = 0;
            let updatedMarginPrev = 0;
            const sortedArray = [visibleEventData];
            sortedArray.forEach((visibleEventData: any, trendIndex: number) => {
                const { eventBasicFields, eventHoverData } = visibleEventData;
                const starttimestampIndex = Math.max(
                    startWeek.findIndex((date: any) => date === eventBasicFields?.startDate),
                    0,
                );
                const endtimestampIndex = Math.max(
                    endWeek.findIndex((date: any) => date === eventBasicFields?.endDate),
                    0,
                );
                const startMonthIndex = Math.floor(starttimestampIndex / weekDivision) * weekDivision;
                const promoType = String(eventHoverData?.tacticDescription).toLowerCase();
                const weekDifference = calculateWeeksDifference(starttimestampIndex, endtimestampIndex);
                const marginLeft = calculateWeeksDifference(startMonthIndex, starttimestampIndex) * weekPercentage;
                const width = `calc(${weekDifference * weekPercentage + weekPercentage}% + ${
                    weekDifference / weekDivision
                }px)`;

                //for index ===0 or if(startmonth index of previous itemis equal to start month index of current item)
                if (trendIndex === 0 || startMonthIndexMapping === startMonthIndex) {
                    updatedMarginTop = marginBlock;
                    updatedMarginPrev += 18; //(padding 10 + height of bar 8)
                    //if starttimestampindex of current item is greater than endtimestamp index of prev item , dont change margin top
                } else if (starttimestampIndex > previousEndIndex) {
                    updatedMarginTop = updatedMarginPrev;
                    //if starttimestampindex of current item is less or equal to endtimestamp index of prev item , change margin top
                } else if (starttimestampIndex <= previousEndIndex) {
                    updatedMarginPrev += 18;
                    updatedMarginTop = updatedMarginPrev;
                }
                previousEndIndex = endtimestampIndex;
                startMonthIndexMapping = startMonthIndex;

                //metric values for the tooltip, keyed by metric name, formatted with
                //the backend-provided pre/post
                const metricInfo: any = {};
                (eventHoverData?.metrics || []).forEach((metric: any) => {
                    metricInfo[metric.name] = _.isNil(metric.value)
                        ? ''
                        : `${metric.pre || ''}${formatNumWithKMBAlt(metric.value, 2, 2)}${metric.post || ''}`;
                });

                const trendObj = {
                    style: {
                        // marginTop: (trendIndex + 1) * marginBlock + (trendIndex + 1) * barHeight,
                        marginTop: trendIndex === 0 ? marginBlock : updatedMarginTop,
                        background:
                            promoType in promoTypeColorMapping
                                ? promoTypeColorMapping[promoType].color
                                : colorConstants.brown,
                        marginLeft: `${marginLeft}%`,
                        width: width,
                    },
                    infoData: {
                        promoMech: eventBasicFields?.eventName,
                        duration:
                            eventHoverData?.duration +
                            (eventHoverData?.duration === 1
                                ? ` ${currentFrequency?.label}`
                                : ` ${currentFrequency?.label}s`),
                        tacticDescription:
                            promoType in promoTypeColorMapping
                                ? promoTypeColorMapping[promoType].label
                                : _.startCase(promoType),
                        dim1AttributeNames: eventHoverData?.dim1AttributeNames?.join(', '),
                        startDate: dayjs(eventBasicFields?.startDate).format(commonConfig?.dateFormatAlt),
                        endTimestamp: dayjs(eventBasicFields?.endDate).format(commonConfig?.dateFormatAlt),
                        ...metricInfo,
                    },
                    shared: false,
                };
                tempObj[dayjs(endWeek[startMonthIndex]).format(commonConfig?.dateFormatAlt)].push(trendObj);
            });

            result.push(tempObj);
        });
        //tooltip item as last item
        result.push({
            event: 'tooltip-calender',
            key: 'tooltip',
        });
        return result;
    } catch (error) {
        console.log(error, 'logging er');
        throw error;
    }
};

function calculateWeeksDifference(startDateIndex: any, endDateIndex: any) {
    return endDateIndex - startDateIndex || 0;
}

export const recommendationKpiTableDataParser = (data: any[], config: any) => {
    const { promoRecommendation, commonConfig } = config;
    const kpiDataKeyMapping: any = promoRecommendation?.overlayKeys;
    const promoTypeColorMapping: any = promoRecommendation?.promoTypeColorMapping || {};
    const leadingColumns = (promoRecommendation?.overlayKeys || []).slice(0, 4);
    try {
        const result = data.map((event: any, idx: number) => {
            const { visibleEventData } = event;
            const eventHoverData = visibleEventData?.eventHoverData || {};
            const metrics = eventHoverData?.metrics || [];
            const nestedResult: any = {};
            const arrowDetails: any = {};
            const isParentIdPresent = _.isFinite(visibleEventData?.eventBasicFields?.parentId);

            //leading descriptive columns (FE config), values read from eventHoverData
            leadingColumns.forEach((item: any) => {
                const value = eventHoverData?.[item.dataKey];
                if (item.dataKey === 'tacticDescription' && value) {
                    nestedResult[item.label] =
                        value in promoTypeColorMapping ? promoTypeColorMapping[value].label : _.startCase(value);
                } else if (item.dataKey === 'startDate' && value) {
                    nestedResult[item.label] = dayjs(value).format(commonConfig?.dateFormatAlt);
                } else {
                    nestedResult[item.label] = Array.isArray(value) ? value.join(', ') : value;
                }
            });

            //metric columns (backend-driven)
            const previousMetrics = isParentIdPresent
                ? data[idx - 1]?.visibleEventData?.eventHoverData?.metrics || []
                : [];
            const previousValueByName: any = {};
            previousMetrics.forEach((metric: any) => {
                previousValueByName[metric.name] = metric.value;
            });

            metrics.forEach((metric: any) => {
                nestedResult[metric.label] = metric.value;
                if (isParentIdPresent) {
                    arrowDetails[metric.label] =
                        metric.value >= previousValueByName[metric.name] ? 'greater' : 'lesser';
                }
            });

            nestedResult['key'] = visibleEventData?.eventBasicFields?.eventId;
            nestedResult['isActive'] = visibleEventData?.eventBasicFields?.activeStatus;
            nestedResult['arrowDetails'] = arrowDetails;

            return nestedResult;
        });
        return result;
    } catch (error) {
        throw error;
    }
};

//summary table data parser - rows are driven by the backend metrics list
//(label, order, pre, post). Values are matched per plan by metric name.
export const summaryDataParserV2 = (data: any[], config: any) => {
    const { promoRecommendation } = config;
    try {
        const result: any[] = [];
        if (data?.length > 0) {
            promoRecommendation.overlayKeys.forEach((item: any) => {
                const tempData: any = {};
                const dataKey = item.dataKey in data?.[0];
                if (dataKey) {
                    tempData.KPI = item.label;
                    data.forEach((dataItem: any) => {
                        const obj = {
                            pre: item.pre,
                            post: item.post,
                            val: dataItem[item.dataKey],
                        };
                        tempData[dataItem['planName']] = obj;
                    });
                    tempData['formatting'] = { pre: item.pre, post: item.post };
                    tempData.dataKey = item.dataKey;
                    result.push(tempData);
                }
            });
        }

        return result;
    } catch (error) {
        throw error;
    }
};

export const summaryDataParser = (data: any[]) => {
        try {
            const result: any[] = [];
            if (data?.length > 0) {
                const metricsTemplate = data?.[0]?.metrics || [];
                metricsTemplate.forEach((metric: any) => {
                    const tempData: any = {};
                    tempData.KPI = metric.label;
                    data.forEach((dataItem: any) => {
                        const planMetric = (dataItem?.metrics || []).find((item: any) => item.name === metric.name);
                        tempData[dataItem['planName']] = {
                            pre: metric.pre || '',
                            post: metric.post || '',
                            val: planMetric?.value ?? null,
                        };
                    });
                    tempData['formatting'] = { pre: metric.pre || '', post: metric.post || '' };
                    tempData.dataKey = metric.name;
                    result.push(tempData);
                });
            }
    
            return result;
        } catch (error) {
            throw error;
        }
};

export const playbookDataParser = (data: any, config: any, currentFrequency: string) => {
    const { promoRecommendation } = config;
    const playbookKeyMapping: any = promoRecommendation.playbookKeyMapping;
    try {
        const result: any[] = [];
        let temp: any = {};
        const frequencyLabel = ` ${frequencyLabelAltMapping[currentFrequency ?? 'W']}`;
        //for total
        Object.keys(playbookKeyMapping).map((item) => {
            temp[item] =
                playbookKeyMapping[item]?.length > 1
                    ? {
                          firstValue: Array.isArray(data?.totalData[playbookKeyMapping[item][0]])
                              ? data?.totalData[playbookKeyMapping[item][0]]
                              : data?.totalData[playbookKeyMapping[item][0]],
                          secondValue: data?.totalData[playbookKeyMapping[item][1]],
                          operator: playbookKeyMapping[item][2],
                          pre: playbookKeyMapping[item][3],
                          post: item === 'Frequency' ? frequencyLabel : playbookKeyMapping[item][4],
                      }
                    : data?.totalData[playbookKeyMapping[item][0]];
        });
        result.push(temp);

        data?.playbookData?.map((data: any, idx: number) => {
            temp = {};
            Object.keys(playbookKeyMapping).map((item) => {
                temp[item] =
                    playbookKeyMapping[item]?.length > 1
                        ? {
                              firstValue: Array.isArray(data[playbookKeyMapping[item][0]])
                                  ? data[playbookKeyMapping[item][0]]
                                  : data[playbookKeyMapping[item][0]],
                              secondValue: data[playbookKeyMapping[item][1]],
                              operator: playbookKeyMapping[item][2],
                              pre: playbookKeyMapping[item][3],
                              post: item === 'Frequency' ? frequencyLabel : playbookKeyMapping[item][4],
                          }
                        : data[playbookKeyMapping[item][0]];
            });
            result.push(temp);
        });
        return result;
    } catch (error) {
        throw error;
    }
};

export function bestWeeksDataParser(responseData: any) {
    // parsing best week
    const weeksPeriodsMappingData: any = [{}];
    if (responseData?.weeksEventsMapping) {
        const weeksMapResponse = JSON.parse(responseData?.weeksEventsMapping);

        monthData.forEach((item: any) => {
            const periods = Object.keys(weeksMapResponse[item.key].periods);
            const newData: any = {
                month: item.label,
                periods: periods,
                events: {},
            };
            periods.forEach((periodNumber: any) => {
                newData.events[periodNumber] = weeksMapResponse[item.key].periods[periodNumber].map((event: any) => {
                    return {
                        eventName: event,
                    };
                });
            });
            weeksPeriodsMappingData.push(newData);
        });
    }

    const bestWeeksData: any = [];
    if (responseData?.weeksData) {
        //  Border logic also should be added
        responseData.weeksData.forEach((item: any) => {
            const newValues: any = {};
            const newData = { attributeName: item.attributeName, values: newValues };
            const parsedValues = JSON.parse(item.values);
            Object.keys(parsedValues).forEach((key: any, index) => {
                newValues[key] = {
                    val: parsedValues[key],
                    color: getBestWeeksColor(parsedValues[key]),
                    isBordered: false, // add some logic
                };
            });
            bestWeeksData.push(newData);
        });
    }

    return { weeksPeriodsMappingData, bestWeeksData: bestWeeksData };
}

const getBestWeeksColor = (value: number) => {
    // for null values return white
    if (!value) return colorConstants.white;

    if (value <= 7) return colorConstants.bestWeeksDarkGreen1;
    if (value <= 14) return colorConstants.bestWeeksDarkGreen2;
    if (value <= 21) return colorConstants.bestWeeksDarkGreen3;
    if (value <= 28) return colorConstants.greyAlt1;
    if (value <= 26) return colorConstants.bestWeeksDarkRed3;
    if (value <= 44) return colorConstants.bestWeeksDarkRed2;
    return colorConstants.bestWeeksDarkRed1;
};

export const recommendationExportParser = (kpiData: any[], eventsListData: any[]) => {
    try {
        const kpiColumnsHeader =
            Array.isArray(kpiData) && kpiData.length > 0 ? Object.keys(kpiData[0]).slice(0, -3) : [];
        const eventsListColumnsHeader =
            Array.isArray(eventsListData) && eventsListData.length > 0
                ? Object.keys(eventsListData[0]).slice(0, -3)
                : [];

        const kpiDataToExport: any[] = [];
        const eventsListDataToExport: any[] = [];

        kpiData?.forEach((item: any) => {
            const tempKpiData: any[] = [];
            kpiColumnsHeader?.forEach((kpi: string) => {
                tempKpiData?.push(item?.[kpi]);
            });
            kpiDataToExport.push(tempKpiData);
        });

        eventsListData?.forEach((item) => {
            const tempEventsListData: any[] = [];
            eventsListColumnsHeader?.forEach((kpi: string) => {
                tempEventsListData?.push(Array.isArray(item?.[kpi]) ? item?.[kpi]?.join(', ') : item?.[kpi]);
            });
            eventsListDataToExport.push(tempEventsListData);
        });
        //merge export data - first events data, then kpi data
        const mergedExportHeader = [...eventsListColumnsHeader, ...kpiColumnsHeader];
        const mergedExportData = eventsListDataToExport?.map((row, i) => [...row, ...kpiDataToExport?.[i]]);
        return { header: mergedExportHeader, data: mergedExportData };
    } catch (error) {
        throw error;
    }
};

// Simple helper function to format constraint values
const formatConstraintValue = (constraint: any, prefix: string = ''): string => {
    const { details, isMinMax, isMultiSelectKpi } = constraint;

    if (isMinMax) {
        const { min, max } = details;
        if (min !== null && min !== undefined && max !== null && max !== undefined) {
            const displayMin = Math.min(min as number, max as number);
            const displayMax = Math.max(min as number, max as number);
            return `${prefix}${formatNumWithKMBAlt(displayMin, 2, 2)} - ${prefix}${formatNumWithKMBAlt(
                displayMax,
                2,
                2,
            )}`;
        } else if (min !== null) {
            return `≥ ${prefix}${formatNumWithKMBAlt(min, 2, 2)}`;
        } else if (max !== null) {
            return `≤ ${prefix}${formatNumWithKMBAlt(max, 2, 2)}`;
        }
    } else if (isMultiSelectKpi && details.multiSelectKpi) {
        return details.multiSelectKpi.map((item: { value: string }) => item.value).join(', ');
    } else if (details.value !== null && details.value !== undefined) {
        return `${prefix}${formatNumWithKMBAlt(details.value, 2, 2)}`;
    }

    return '-';
};

// Simple mapping function to map plans array to table structure
const mapPlansToTableData = (plans: any[], constraintRows: any[]): any[] => {
    return constraintRows.map((row) => {
        const rowData: Record<string, any> = {
            constraint: row.constraint,
        };

        plans.forEach((plan) => {
            let value = '-';

            if (row.key === 'objective') {
                // Find the objective constraint
                const objectiveConstraint = plan.constraints?.find((c: any) => c.objective);
                if (objectiveConstraint) {
                    // Find the KPI name that matches the objective
                    const kpiName = objectiveConstraint?.objective;
                    const matchingRow = constraintRows.find((r) => r?.value === kpiName);
                    value = matchingRow?.constraint || '-';
                }
            } else {
                // Find constraint that matches this row's key
                const matchingConstraint = plan.constraints?.find((c: any) => c.kpi === row.key);

                if (matchingConstraint) {
                    value = formatConstraintValue(matchingConstraint, row.prefix);
                }
            }

            rowData[plan.plan_name] = value;
        });

        return rowData;
    });
};

export const planConstraintsParser = (data: any, constraintRows: any): any | null => {
    try {
        const { plans, total_plans } = data;
        if (!plans || !Array.isArray(plans) || plans.length === 0) {
            return null;
        }

        const tableData = mapPlansToTableData(plans, constraintRows);

        return {
            plans,
            total_plans: total_plans || 0,
            constraints: tableData,
        };
    } catch (error) {
        throw error;
    }
};
